import React from 'react'
import { Sidebar, Button, Text, Input, LoadingSpinner, Heading } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { crmService } from '../../services/crm'
import { CRMContact, CRMCompany } from '../../types/crm'

const CRMContatos: React.FC = () => {
  const [loading, setLoading] = React.useState<boolean>(true)
  const [contacts, setContacts] = React.useState<CRMContact[]>([])
  const [companies, setCompanies] = React.useState<CRMCompany[]>([])
  const [query, setQuery] = React.useState<string>('')

  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [editing, setEditing] = React.useState<Partial<CRMContact> | null>(null)

  const load = React.useCallback(async () => {
    setLoading(true)
    const [cts, cos] = await Promise.all([crmService.listContacts(), crmService.listCompanies()])
    setContacts(cts)
    setCompanies(cos)
    setLoading(false)
  }, [])

  React.useEffect(() => { load() }, [load])

  const filtered = React.useMemo(() => {
    if (!query) return contacts
    const q = query.toLowerCase()
    return contacts.filter(c =>
      c.full_name.toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.phone || '').toLowerCase().includes(q) ||
      (c.company?.name || '').toLowerCase().includes(q)
    )
  }, [contacts, query])

  const openNew = () => {
    setEditing({ full_name: '', email: '', phone: '', position: '', company_id: null })
    setIsModalOpen(true)
  }

  const openEdit = (c: CRMContact) => {
    setEditing({ ...c })
    setIsModalOpen(true)
  }

  const save = async () => {
    if (!editing?.full_name) return
    await crmService.upsertContact(editing as any)
    setIsModalOpen(false)
    setEditing(null)
    await load()
  }

  const remove = async (id: string) => {
    if (!window.confirm('Excluir este contato?')) return
    await crmService.deleteContact(id)
    await load()
  }

  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto">
        <PermissionGuard moduleName="crm_contatos" permissionType="show">
          <div className="py-6 px-4 sm:px-6 lg:px-8">
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">Contatos</Heading>
                <Text size="sm" color="muted" className="mt-2">Gerencie contatos do CRM</Text>
              </div>
              <PermissionGuard moduleName="crm_contatos" permissionType="insert" fallback={null}>
                <Button variant="solid" onClick={openNew}>Novo contato</Button>
              </PermissionGuard>
            </div>
            {/* Filtros e busca */}
            <div className="mb-4 flex items-center gap-3">
              <div className="w-full sm:w-80">
                <Input
                  placeholder="Buscar por nome, e-mail, telefone ou empresa..."
                  value={query}
                  onChange={(e: any) => setQuery(e.target.value)}
                />
              </div>
            </div>

            {/* Tabela */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900/30">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Empresa</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">E-mail</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Telefone</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center">
                        <LoadingSpinner />
                      </td>
                    </tr>
                  ) : filtered.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                        Nenhum contato encontrado.
                      </td>
                    </tr>
                  ) : (
                    filtered.map((c) => (
                      <tr key={c.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{c.full_name}</td>
                        <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">{c.company?.name || '—'}</td>
                        <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">{c.email || '—'}</td>
                        <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">{c.phone || '—'}</td>
                        <td className="px-4 py-2 text-right space-x-2">
                          <PermissionGuard moduleName="crm_contatos" permissionType="update" fallback={null}>
                            <Button variant="outline" size="sm" onClick={() => openEdit(c)}>Editar</Button>
                          </PermissionGuard>
                          <PermissionGuard moduleName="crm_contatos" permissionType="delete" fallback={null}>
                            <Button variant="ghost" size="sm" onClick={() => remove(c.id)}>Excluir</Button>
                          </PermissionGuard>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Modal simples */}
            {isModalOpen && editing && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <Text size="lg" className="text-gray-900 dark:text-white">
                      {editing?.id ? 'Editar contato' : 'Novo contato'}
                    </Text>
                    <Button variant="ghost" onClick={() => { setIsModalOpen(false); setEditing(null) }}>Fechar</Button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Nome</Text>
                      <Input
                        value={editing.full_name || ''}
                        onChange={(e: any) => setEditing((prev) => ({ ...prev!, full_name: e.target.value }))}
                        placeholder="Nome completo"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">E-mail</Text>
                        <Input
                          value={editing.email || ''}
                          onChange={(e: any) => setEditing((prev) => ({ ...prev!, email: e.target.value }))}
                          placeholder="email@exemplo.com"
                        />
                      </div>
                      <div>
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Telefone</Text>
                        <Input
                          value={editing.phone || ''}
                          onChange={(e: any) => setEditing((prev) => ({ ...prev!, phone: e.target.value }))}
                          placeholder="(00) 00000-0000"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Cargo</Text>
                        <Input
                          value={editing.position || ''}
                          onChange={(e: any) => setEditing((prev) => ({ ...prev!, position: e.target.value }))}
                          placeholder="Cargo"
                        />
                      </div>
                      <div>
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Empresa</Text>
                        <select
                          className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                          value={editing.company_id || ''}
                          onChange={(e) => setEditing((prev) => ({ ...prev!, company_id: e.target.value || null }))}
                        >
                          <option value="">—</option>
                          {companies.map((co) => (
                            <option key={co.id} value={co.id}>{co.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end gap-2">
                    <Button variant="ghost" onClick={() => { setIsModalOpen(false); setEditing(null) }}>Cancelar</Button>
                    <Button variant="solid" onClick={save}>Salvar</Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </PermissionGuard>
      </div>
    </div>
  )
}

export default CRMContatos
